import React, { useState, useEffect, useCallback } from "react";
import {
  Shield, Plus, Trash2, Pause, Play, Edit2, Download, X, Check, Users,
  Phone, Key, ChevronDown, RefreshCw, UserPlus, AlertTriangle, FileSpreadsheet,
  Database, AlertCircle, CheckCircle
} from "lucide-react";
import { logger } from "../utils/logger";
import { AddAdminModal, AddOpsModal, EditMemberModal, ClearConfirmModal } from "./rbac/RBACModals";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_COLORS = {
  admin: "bg-[#E83E8C] text-white",
  cfo: "bg-emerald-500 text-white",
  ops_head: "bg-blue-500 text-white",
  sales_manager: "bg-orange-500 text-white",
  sales_executive: "bg-amber-500 text-white",
  security_manager: "bg-gray-500 text-white",
  manager: "bg-purple-500 text-white",
  ops_executive: "bg-teal-500 text-white",
};

const RBACManagementTab = ({ auth, authAxios }) => {
  const [members, setMembers] = useState([]);
  const [opsMembers, setOpsMembers] = useState([]);
  const [roles, setRoles] = useState({});
  const [allTabs, setAllTabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [showAddOps, setShowAddOps] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [editOps, setEditOps] = useState(null);
  const [form, setForm] = useState({ username: "", password: "", name: "", role: "manager", department: "" });
  const [opsForm, setOpsForm] = useState({ name: "", phone: "", pin: "1234", role: "decorator" });
  const [dataCategories, setDataCategories] = useState([]);
  const [selectedCollections, setSelectedCollections] = useState(new Set());
  const [clearing, setClearing] = useState(false);
  const [clearResults, setClearResults] = useState(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const fetchMembers = useCallback(async () => {
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/rbac/members`);
      setMembers(r.data.admin_members || []);
      setOpsMembers(r.data.ops_members || []);
      setRoles(r.data.available_roles || {});
      setAllTabs(r.data.all_tabs || []);
    } catch (err) { logger.error("[RBAC] Fetch error:", err.message); }
    setLoading(false);
  }, [authAxios]);

  const fetchDataCollections = useCallback(async () => {
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/rbac/data-collections`);
      setDataCategories(r.data.categories || []);
    } catch (err) { logger.error("[RBAC] Data fetch error:", err.message); }
  }, [authAxios]);

  useEffect(() => { fetchMembers(); fetchDataCollections(); }, [fetchMembers, fetchDataCollections]);

  const handleAddAdmin = async () => {
    if (!form.username || !form.password) return;
    try {
      await authAxios.post(`${API_URL}/api/admin/rbac/add-member`, form);
      setShowAddAdmin(false);
      setForm({ username: "", password: "", name: "", role: "manager", department: "" });
      fetchMembers();
    } catch (err) { alert(err.response?.data?.detail || "Failed to add"); }
  };

  const handleToggleActive = async (username, currentActive) => {
    try {
      await authAxios.put(`${API_URL}/api/admin/rbac/member/${username}`, { active: !currentActive });
      fetchMembers();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  const handleDeleteAdmin = async (username) => {
    if (!window.confirm(`Delete ${username}? This cannot be undone.`)) return;
    try {
      await authAxios.delete(`${API_URL}/api/admin/rbac/member/${username}`);
      fetchMembers();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  const handleUpdateMember = async () => {
    if (!editMember) return;
    try {
      const payload = { role: editMember.role, name: editMember.name, department: editMember.department };
      if (editMember.newPassword) payload.password = editMember.newPassword;
      await authAxios.put(`${API_URL}/api/admin/rbac/member/${editMember.username}`, payload);
      setEditMember(null);
      fetchMembers();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  const handleAddOps = async () => {
    if (!opsForm.name || !opsForm.phone) return;
    try {
      await authAxios.post(`${API_URL}/api/admin/rbac/add-ops`, opsForm);
      setShowAddOps(false);
      setOpsForm({ name: "", phone: "", pin: "1234", role: "decorator" });
      fetchMembers();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  const handleToggleOps = async (phone, currentActive) => {
    try {
      await authAxios.put(`${API_URL}/api/admin/rbac/ops/${phone}`, { is_active: !currentActive });
      fetchMembers();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  const handleDeleteOps = async (phone) => {
    if (!window.confirm("Delete this ops member?")) return;
    try {
      await authAxios.delete(`${API_URL}/api/admin/rbac/ops/${phone}`);
      fetchMembers();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  const toggleCollection = (name) => {
    setSelectedCollections(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toggleCategory = (collections) => {
    const deletable = collections.filter(c => !c.protected);
    const allSelected = deletable.every(c => selectedCollections.has(c.name));
    setSelectedCollections(prev => {
      const next = new Set(prev);
      deletable.forEach(c => allSelected ? next.delete(c.name) : next.add(c.name));
      return next;
    });
  };

  const handleClearData = async () => {
    if (selectedCollections.size === 0) return;
    setClearing(true);
    setClearResults(null);
    try {
      const r = await authAxios.post(`${API_URL}/api/admin/rbac/clear-data`, {
        collections: Array.from(selectedCollections),
      });
      setClearResults(r.data.results);
      setSelectedCollections(new Set());
      fetchDataCollections();
    } catch (err) { alert(err.response?.data?.detail || "Failed to clear data"); }
    setClearing(false);
    setShowClearConfirm(false);
  };

  if (auth.role !== "admin") {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <AlertTriangle size={48} className="text-amber-500 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-[#1E1B4B]">Super Admin Only</h3>
          <p className="text-sm text-[#94a3b8]">RBAC management requires Super Admin access</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rbac-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold text-[#1E1B4B] flex items-center gap-2">
          <Shield size={20} className="text-[#E83E8C]" /> Team & Access Control
        </h2>
        <div className="flex gap-2">
          <button onClick={() => { setLoading(true); fetchMembers(); }} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-sm text-[#475569] hover:bg-gray-50" data-testid="rbac-refresh">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <a href="/DoozyGo_RBAC_Credentials.pdf" download className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#E83E8C]/10 text-[#E83E8C] text-sm font-medium hover:bg-[#E83E8C]/20" data-testid="rbac-download-pdf">
            <Download size={14} /> PDF
          </a>
          <a href="/DoozyGo_RBAC_Credentials.xlsx" download className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-sm font-medium hover:bg-emerald-100" data-testid="rbac-download-excel">
            <FileSpreadsheet size={14} /> Excel
          </a>
        </div>
      </div>

      {/* ═══ Admin Panel Members ═══ */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="font-bold text-[#1E1B4B] flex items-center gap-2"><Users size={16} /> Admin Panel Members ({members.length})</h3>
          <button onClick={() => setShowAddAdmin(true)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#E83E8C] text-white text-sm font-medium hover:bg-[#d62f7a]" data-testid="rbac-add-admin">
            <Plus size={14} /> Add Member
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#1E1B4B]/5 text-xs text-[#475569] uppercase tracking-wider">
                <th className="p-3 text-left">Username</th>
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Role</th>
                <th className="p-3 text-left">Access Tabs</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map(m => (
                <tr key={m.username} className={`border-b border-gray-50 hover:bg-[#FFFAF5] ${!m.active ? 'opacity-50' : ''}`} data-testid={`rbac-row-${m.username}`}>
                  <td className="p-3 font-mono text-xs font-medium text-[#1E1B4B]">{m.username}</td>
                  <td className="p-3 text-[#475569]">{m.name}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${ROLE_COLORS[m.role] || "bg-gray-200 text-gray-700"}`}>
                      {m.role_label}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-[#94a3b8] max-w-[250px]">
                    <div className="flex flex-wrap gap-1">
                      {(m.allowed_tabs || []).map(t => (
                        <span key={t} className="px-1.5 py-0.5 bg-[#1E1B4B]/5 rounded text-[9px] font-medium">{t}</span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${m.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                      {m.active ? 'Active' : 'Paused'}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => setEditMember({ ...m, newPassword: "" })} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-500" title="Edit" data-testid={`rbac-edit-${m.username}`}>
                        <Edit2 size={14} />
                      </button>
                      <button onClick={() => handleToggleActive(m.username, m.active)} className={`p-1.5 rounded-lg ${m.active ? 'hover:bg-amber-50 text-amber-500' : 'hover:bg-green-50 text-green-500'}`} title={m.active ? "Pause" : "Resume"} data-testid={`rbac-toggle-${m.username}`}>
                        {m.active ? <Pause size={14} /> : <Play size={14} />}
                      </button>
                      <button onClick={() => handleDeleteAdmin(m.username)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400" title="Delete" data-testid={`rbac-delete-${m.username}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══ Ops Team Members ═══ */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="font-bold text-[#1E1B4B] flex items-center gap-2"><Phone size={16} className="text-[#FF9F1C]" /> Ops Team / Decorators ({opsMembers.length})</h3>
          <button onClick={() => setShowAddOps(true)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#FF9F1C] text-white text-sm font-medium hover:bg-[#e88f18]" data-testid="rbac-add-ops">
            <UserPlus size={14} /> Add Decorator
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#FF9F1C]/5 text-xs text-[#475569] uppercase tracking-wider">
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Phone</th>
                <th className="p-3 text-left">Role</th>
                <th className="p-3 text-center">PIN</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {opsMembers.map(o => (
                <tr key={o.phone} className={`border-b border-gray-50 hover:bg-[#FFFAF5] ${!o.active ? 'opacity-50' : ''}`} data-testid={`rbac-ops-${o.phone}`}>
                  <td className="p-3 font-medium text-[#1E1B4B]">{o.name}</td>
                  <td className="p-3 font-mono text-xs">{o.phone}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${o.role === 'ops_manager' ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}`}>
                      {o.role_label}
                    </span>
                  </td>
                  <td className="p-3 text-center font-mono text-xs text-[#E83E8C]">{o.pin}</td>
                  <td className="p-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${o.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                      {o.active ? 'Active' : 'Paused'}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => handleToggleOps(o.phone, o.active)} className={`p-1.5 rounded-lg ${o.active ? 'hover:bg-amber-50 text-amber-500' : 'hover:bg-green-50 text-green-500'}`} title={o.active ? "Pause" : "Resume"}>
                        {o.active ? <Pause size={14} /> : <Play size={14} />}
                      </button>
                      <button onClick={() => handleDeleteOps(o.phone)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400" title="Delete">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══ Data Management Section ═══ */}
      <div className="bg-white rounded-2xl shadow-lg border border-red-100 overflow-hidden" data-testid="data-management">
        <div className="flex items-center justify-between p-4 border-b border-red-100 bg-red-50/30">
          <h3 className="font-bold text-red-700 flex items-center gap-2">
            <Database size={16} /> Data Management
            <span className="text-xs font-normal text-red-400">Select collections to clear</span>
          </h3>
          {selectedCollections.size > 0 && (
            <button
              onClick={() => setShowClearConfirm(true)}
              disabled={clearing}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-bold hover:bg-red-600 disabled:opacity-50"
              data-testid="clear-data-btn"
            >
              <Trash2 size={14} /> Clear {selectedCollections.size} Selected
            </button>
          )}
        </div>

        {/* Clear Results */}
        {clearResults && (
          <div className="p-3 bg-green-50 border-b border-green-100">
            <p className="text-xs font-bold text-green-700 mb-1 flex items-center gap-1"><CheckCircle size={12} /> Data cleared successfully:</p>
            <div className="flex flex-wrap gap-1">
              {Object.entries(clearResults).map(([col, result]) => (
                <span key={col} className={`px-2 py-0.5 rounded text-[10px] font-medium ${result.includes('PROTECTED') ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                  {col}: {result}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="p-4 space-y-4">
          {dataCategories.map(cat => {
            const deletable = cat.collections.filter(c => !c.protected);
            const allSelected = deletable.length > 0 && deletable.every(c => selectedCollections.has(c.name));
            const totalDocs = cat.collections.reduce((s, c) => s + c.count, 0);
            return (
              <div key={cat.category}>
                <div className="flex items-center gap-2 mb-2">
                  <button
                    onClick={() => toggleCategory(cat.collections)}
                    className={`w-4 h-4 rounded border flex items-center justify-center ${allSelected ? 'bg-red-500 border-red-500' : 'border-gray-300'}`}
                    data-testid={`cat-select-${cat.category.replace(/\s/g, '-')}`}
                  >
                    {allSelected && <Check size={10} className="text-white" />}
                  </button>
                  <span className="text-xs font-bold text-[#1E1B4B] uppercase tracking-wider">{cat.category}</span>
                  <span className="text-[10px] text-[#94a3b8]">({totalDocs} total docs)</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 ml-6">
                  {cat.collections.map(col => {
                    const isSelected = selectedCollections.has(col.name);
                    return (
                      <button
                        key={col.name}
                        onClick={() => !col.protected && toggleCollection(col.name)}
                        disabled={col.protected}
                        className={`flex items-center justify-between px-3 py-2 rounded-lg border text-left transition-all ${
                          col.protected
                            ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                            : isSelected
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-200 hover:border-red-200'
                        }`}
                        data-testid={`col-${col.name}`}
                      >
                        <div>
                          <p className={`text-xs font-medium ${isSelected ? 'text-red-700' : 'text-[#1E1B4B]'}`}>{col.name}</p>
                          <p className="text-[10px] text-[#94a3b8]">{col.count} docs</p>
                        </div>
                        {col.protected ? (
                          <span className="text-[9px] text-amber-500 font-medium">Protected</span>
                        ) : isSelected ? (
                          <div className="w-4 h-4 rounded bg-red-500 flex items-center justify-center"><Check size={10} className="text-white" /></div>
                        ) : (
                          <div className="w-4 h-4 rounded border border-gray-300" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="p-3 border-t border-red-100 bg-red-50/30 text-[10px] text-red-400 flex items-center gap-1">
          <AlertCircle size={10} /> Protected collections (team accounts, bot knowledge, pincodes, system config) cannot be deleted.
        </div>
      </div>

      <ClearConfirmModal show={showClearConfirm} onClose={() => setShowClearConfirm(false)} selectedCollections={selectedCollections} clearing={clearing} onConfirm={handleClearData} />
      <AddAdminModal show={showAddAdmin} onClose={() => setShowAddAdmin(false)} form={form} setForm={setForm} roles={roles} onSubmit={handleAddAdmin} />
      <AddOpsModal show={showAddOps} onClose={() => setShowAddOps(false)} form={opsForm} setForm={setOpsForm} onSubmit={handleAddOps} />
      <EditMemberModal member={editMember} setMember={setEditMember} roles={roles} onSubmit={handleUpdateMember} />
    </div>
  );
};

export default RBACManagementTab;
